import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogTitle from '@mui/material/DialogTitle';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import { useEffect, useState, type ReactElement, type SubmitEvent } from 'react';
import { useTranslation } from 'react-i18next';

type InviteMemberDialogProps = {
    open: boolean;
    companyName: string;
    isSubmitting: boolean;
    errorMessage: string | undefined;
    onSubmit: (email: string) => void;
    onCancel: () => void;
};

export function InviteMemberDialog({
    open,
    companyName,
    isSubmitting,
    errorMessage,
    onSubmit,
    onCancel,
}: InviteMemberDialogProps): ReactElement {
    const { t } = useTranslation();
    const [email, setEmail] = useState('');

    // Reset the field whenever the dialog is (re)opened so a previous entry never lingers.
    useEffect(() => {
        if (open) {
            setEmail('');
        }
    }, [open]);

    const handleSubmit = (event: SubmitEvent<HTMLFormElement>): void => {
        event.preventDefault();
        onSubmit(email.trim());
    };

    return (
        <Dialog
            open={open}
            onClose={onCancel}
            aria-labelledby="invite-member-dialog-title"
            aria-describedby="invite-member-dialog-description"
            fullWidth
            maxWidth={false}
            slotProps={{ paper: { sx: { maxWidth: 708, m: { xs: 2, md: 4 } } } }}>
            <Box component="form" onSubmit={handleSubmit}>
                <DialogTitle id="invite-member-dialog-title">
                    {t('SETTINGS.MEMBERS.INVITE_DIALOG.TITLE', { companyName })}
                </DialogTitle>
                <DialogContent>
                    <Stack spacing={2}>
                        <DialogContentText id="invite-member-dialog-description" sx={{ color: 'text.primary' }}>
                            {t('SETTINGS.MEMBERS.INVITE_DIALOG.DESCRIPTION')}
                        </DialogContentText>
                        <Typography variant="caption" component="p" sx={{ color: 'text.primary', fontWeight: 600 }}>
                            {t('SETTINGS.MEMBERS.INVITE_DIALOG.ADMIN_NOTE')}
                        </Typography>
                        {errorMessage ? <Alert severity="error">{errorMessage}</Alert> : null}
                        <TextField
                            label={t('SETTINGS.MEMBERS.INVITE_DIALOG.EMAIL_LABEL')}
                            placeholder={t('SETTINGS.MEMBERS.INVITE_DIALOG.EMAIL_PLACEHOLDER')}
                            type="email"
                            value={email}
                            onChange={(event) => setEmail(event.target.value)}
                            required
                            fullWidth
                            autoComplete="email"
                        />
                    </Stack>
                </DialogContent>
                <DialogActions>
                    <Button onClick={onCancel} color="inherit">
                        {t('SETTINGS.MEMBERS.INVITE_DIALOG.CANCEL')}
                    </Button>
                    <Button
                        type="submit"
                        variant="contained"
                        disabled={isSubmitting || email.trim().length === 0}
                        startIcon={isSubmitting ? <CircularProgress size={16} color="inherit" /> : undefined}>
                        {t('SETTINGS.MEMBERS.INVITE_DIALOG.SEND')}
                    </Button>
                </DialogActions>
            </Box>
        </Dialog>
    );
}
